import { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Star, Search, Plus, X, RefreshCw, TrendingUp, TrendingDown, DollarSign, Layers, Sparkles, Loader, AlertTriangle } from 'lucide-react';
import { apiGetFavorites, apiAddFavorite, apiRemoveFavorite, apiEvaluateSignals } from '../utils/backendApi';
import { getBatchTechnicals, getBatchQuotes, getQuote, scoreTickerSignal, computeTargetStop, evaluateSignalGates } from '../utils/marketEngine';
import { apiGetVix } from '../utils/backendApi';
import { validateSignalsBatch } from '../utils/validateSignalsBatch';
import { searchSymbols, findBestOption, getOptionContractQuote } from '../utils/polygonService';
import { useExchange } from '../context/ExchangeContext';
import { formatDateOnly } from '../utils/format';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import DirBadge from '../components/ui/DirBadge';
import ConfBar from '../components/ui/ConfBar';
import { TERMS } from '../utils/terms';
import { useToast } from '../components/ui/Toast';

const displayTicker = (t) => t || '';

export default function FavoritesPage() {
  const { setSelectedTicker, openTrade } = useOutletContext();
  const navigate = useNavigate();
  const { config } = useExchange();
  const [favorites, setFavorites] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [signals, setSignals] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const [validateDone, setValidateDone]   = useState(0);
  const [validateTotal, setValidateTotal] = useState(0);
  const cancelValidationRef = useRef(null);
  const [tradingTicker, setTradingTicker] = useState(null); // ticker currently being pre-validated for trade
  const [tradeError, setTradeError] = useState({}); // { [ticker]: errorMessage }
  const [aiInsights, setAiInsights] = useState({});
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const searchTimer = useRef(null);

  const loadFavorites = useCallback(async () => {
    setLoading(true);
    setQuotes({});
    setSignals({});
    try {
      const favs = await apiGetFavorites();
      setFavorites(favs);

      // ── Pass 1: batch all quotes — add SPY for index RS calculation ──────
      const favTickers = favs.map(f => f.ticker);
      const needSpy = !favTickers.includes('SPY');
      const batchResult = await getBatchQuotes(needSpy ? [...favTickers, 'SPY'] : favTickers);
      const q = {};
      for (const fav of favs) q[fav.ticker] = batchResult[fav.ticker] || null;
      setQuotes(q);

      // index changePct for RS calculation: SPY daily move
      const indexChangePct = batchResult['SPY']?.changePct || 0;

      // ── Pass 2: batch technicals — 1 call regardless of how many favorites.
      // Include SPY so we can derive the bull/bear regime (SPY vs its EMA50),
      // which the shared gate evaluator needs to flag counter-trend
      // suppression exactly the way the Signals page does. ──
      const techMap = await getBatchTechnicals(needSpy ? [...favTickers, 'SPY'] : favTickers);

      // Regime context — mirrors generateTradeSignals so evaluateSignalGates
      // produces identical verdicts. VIX is best-effort; null → 'normal'.
      const spyPrice = batchResult['SPY']?.price ?? 0;
      const spyEma50 = techMap['SPY']?.ema50 ?? null;
      const regimeBull = spyEma50 != null && spyPrice > 0 ? spyPrice >= spyEma50 : true;
      const vixLevel = await apiGetVix().then(r => r?.vix ?? null).catch(() => null);
      const vixRegime = vixLevel == null ? 'normal'
                      : vixLevel < 18    ? 'calm'
                      : vixLevel >= 25   ? 'stressed'
                      : 'normal';

      // ── Pass 3: compute signals using the shared scorer (same as Signals page) ─
      const s = {};
      const optionCandidates = [];

      for (const fav of favs) {
        const tech  = techMap[fav.ticker] || {};
        const quote = q[fav.ticker] || {};
        const price = quote.price || 0;

        const scored = scoreTickerSignal(quote, tech, { indexChangePct });
        // Apply the same lenient gates as before: weak or undirected → HOLD.
        const dir = (scored.score >= 4 && scored.confidence >= 55) ? scored.direction : 'HOLD';
        // Then ask the SHARED Signals-page gate evaluator whether this
        // directional signal would actually ship. When it wouldn't, we
        // surface the reason on the card so the watchlist never silently
        // contradicts the Signals page.
        const gateVerdict = dir === 'HOLD'
          ? null
          : evaluateSignalGates(scored, { regimeBull, vixRegime, tech, price });
        const ts  = computeTargetStop(quote, tech, dir === 'HOLD' ? 'LONG' : dir);

        s[fav.ticker] = {
          ticker: fav.ticker, currentPrice: price,
          direction: dir, reasons: scored.reasons, score: scored.score,
          confidence: scored.confidence, option: null,
          targetPct: dir === 'HOLD' ? 0 : ts.targetPct,
          stopPct:   dir === 'HOLD' ? 0 : ts.stopPct,
          // Absolute prices the auto-close monitor needs — directly from
          // the same computeTargetStop the SignalCard uses.
          targetPrice: dir === 'HOLD' ? null : ts.targetPrice,
          stopLoss:    dir === 'HOLD' ? null : ts.stopLoss,
          riskReward: ts.riskReward,
          timeframe: ts.timeframe,
          days: ts.holdingDays,
          _confidenceSource: dir === 'HOLD' ? 'heuristic' : 'pending',
          // Signals-page ship verdict for this directional signal. Drives
          // the "not in today's signals" badge below.
          _gateShips:  gateVerdict ? gateVerdict.ships : null,
          _gateReason: gateVerdict && !gateVerdict.ships ? gateVerdict.detail : null,
        };

        if (dir !== 'HOLD' && price > 0) {
          optionCandidates.push(fav.ticker);
        }
      }

      // ── Render immediately — options load progressively in background ────────
      setSignals({ ...s });
      setLoading(false);

      // ── Pass 3.5: auto-validate every directional signal in parallel ────────
      // Each card flips from VALIDATING → VALIDATED / LIMITED DATA / HEURISTIC
      // as its backtest settles. Cancel-flag flips on each reload to avoid
      // stale writes racing a fresh load.
      if (cancelValidationRef.current) cancelValidationRef.current();
      let cancelled = false;
      cancelValidationRef.current = () => { cancelled = true; };
      const directional = Object.values(s).filter(sig => sig.direction !== 'HOLD');
      setValidateTotal(directional.length);
      setValidateDone(0);
      validateSignalsBatch(
        directional,
        ({ ticker, status, result, done }) => {
          if (cancelled) return;
          setSignals(prev => {
            const existing = prev[ticker];
            if (!existing) return prev;
            if (status === 'validated') {
              return { ...prev, [ticker]: {
                ...existing,
                confidence: result.calibratedConfidence,
                calibratedConfidence: result.calibratedConfidence,
                _confidenceSource: 'backtest',
                _backtestResult: result,
              }};
            }
            return { ...prev, [ticker]: { ...existing, _confidenceSource: status } };
          });
          setValidateDone(done);
        },
        { isCancelled: () => cancelled }
      ).then(() => { if (!cancelled) setValidateTotal(0); });

      // ── Pass 4: options chains — 3 concurrent Polygon calls at a time ────────
      // Each card updates individually as its option data arrives, so the user
      // sees ALL favorites get options (not just the first N), without blasting
      // Polygon with dozens of simultaneous requests.
      const CONCURRENCY = 3;
      for (let i = 0; i < optionCandidates.length; i += CONCURRENCY) {
        const batch = optionCandidates.slice(i, i + CONCURRENCY);
        await Promise.all(batch.map(async (ticker) => {
          const sig   = s[ticker];
          const price = q[ticker]?.price || 0;
          try {
            const realOpt = await findBestOption(ticker, sig.direction, price);
            if (realOpt) {
              const optType = sig.direction === 'LONG' ? 'CALL' : 'PUT';
              const iv      = realOpt.iv ? +(realOpt.iv * 100).toFixed(1) : 30;
              const option  = {
                type: optType, strike: realOpt.strike, symbol: realOpt.symbol ?? null,
                premium: realOpt.midpoint || realOpt.last,
                expiration: realOpt.expiration,
                expDays: Math.round((new Date(realOpt.expiration) - new Date()) / 86400000),
                bid: realOpt.bid, ask: realOpt.ask, delta: realOpt.delta, iv,
                strategy: iv > 50 ? 'High IV — consider debit spread' : iv > 35 ? 'Moderate IV — debit spread' : iv < 18 ? 'Low IV — direct buy' : 'Balanced IV',
                _source: 'polygon',
              };
              // Update just this card — other cards stay as-is
              setSignals(prev => ({ ...prev, [ticker]: { ...prev[ticker], option } }));
            }
          } catch { /* no option data for this ticker */ }
        }));
      }
    } catch (e) {
      console.error('Failed to load watchlist:', e);
      setLoading(false);
    }
  }, []);

  // Cancel any in-flight validation on unmount.
  useEffect(() => () => {
    if (cancelValidationRef.current) cancelValidationRef.current();
  }, []);

  const handleAiAnalysis = useCallback(async () => {
    const scoredSignals = Object.entries(signals)
      .filter(([, s]) => s && s.direction !== 'HOLD')
      .map(([ticker, s]) => ({
        ticker,
        direction:  s.direction,
        confidence: s.confidence,
        changePct:  s.changePct,
        reasons:    s.reasons,
      }));
    if (aiLoading || scoredSignals.length === 0) return;
    setAiLoading(true);
    setAiError(null);
    setAiSummary(null);
    try {
      const result = await apiEvaluateSignals(scoredSignals);
      const map = {};
      for (const a of result.adjustments ?? []) map[a.ticker] = a;
      setAiInsights(map);
      setAiSummary(result.summary ?? null);
    } catch (e) {
      setAiError(e.message || 'AI analysis failed');
    }
    setAiLoading(false);
  }, [signals, aiLoading]);

  useEffect(() => { loadFavorites(); }, [loadFavorites]);

  // ── Symbol search — Polygon symbol search via server proxy ──
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (query.length < 1) { setSearchResults([]); return; }

    searchTimer.current = setTimeout(async () => {
      try {
        const results = await searchSymbols(query);
        if (results?.length) { setSearchResults(results); return; }
      } catch { /* fall through to curated list */ }
      // Fallback: curated list
      const q = query.toUpperCase();
      const fallback = [
        'AAPL','MSFT','GOOGL','AMZN','META','NVDA','TSLA','SPY','QQQ',
        'AMD','NFLX','JPM','COIN','PLTR',
      ].filter(t => t.includes(q)).slice(0, 8).map(t => ({ ticker: t, name: t, type: 'Stock' }));
      setSearchResults(fallback);
    }, 300);
  };

  const handleAdd = async (ticker, name) => {
    try {
      await apiAddFavorite(ticker, name);
      toast.success(`${ticker} added to watchlist`);
      setSearchQuery('');
      setSearchResults([]);
      loadFavorites();
    } catch (e) { toast.error(e.message); }
  };

  const handleRemove = async (ticker) => {
    try {
      await apiRemoveFavorite(ticker);
      setFavorites(prev => prev.filter(f => f.ticker !== ticker));
      toast.info(`${ticker} removed`);
    } catch (e) { toast.error(e.message); }
  };

  const handleTrade = async (ticker, direction, type = 'stock', option = null) => {
    if (tradingTicker === ticker) return; // already validating this ticker
    setTradingTicker(ticker);
    setTradeError(prev => ({ ...prev, [ticker]: null }));
    try {
      // getQuote checks quoteCache first (15 s TTL) — no extra API call if batch just ran
      const q = await getQuote(ticker);
      if (!q?.price) throw new Error('No quote returned');

      let price = q.price;
      let premium = option?.premium;

      if (type === 'option' && option?._source === 'polygon' && option?.symbol) {
        try {
          const optQ = await getOptionContractQuote(option.symbol);
          if (optQ?.midpoint != null) premium = optQ.midpoint;
        } catch {
          // Option quote unavailable — proceed with signal premium
        }
      }

      // Pull the targets the scorer computed for THIS direction so they
      // prefill the auto-close fields on the trade form.
      const sig    = signals[ticker];
      const stopP  = type === 'option'
        ? (Number.isFinite(premium) && premium > 0 ? +(premium * 0.5).toFixed(4) : null)
        : (sig?.stopLoss ?? null);
      const tgtP   = type === 'option'
        ? (Number.isFinite(premium) && premium > 0 ? +(premium * 2.0).toFixed(4) : null)
        : (sig?.targetPrice ?? null);

      // Open the shared NewTradeModal at Layout level instead of navigating
      // to /dashboard/trading — keeps the user on the Watchlist.
      openTrade({
        ticker,
        direction: direction === 'LONG' ? 'BUY' : direction === 'SHORT' ? 'SHORT' : 'BUY',
        price: type === 'option' && option ? (premium ?? option.premium) : price,
        type,
        optionType:           option?.type,
        optionStrike:         option?.strike,
        optionExpiry:         option?.expiration,
        optionContractSymbol: option?._source === 'polygon' ? (option?.symbol ?? null) : null,
        signalSource:         option ? `AI Signal · Watchlist` : '',
        stopPrice:            stopP,
        targetPrice:          tgtP,
        swingDays:            sig?.days || sig?.holdingDays || null,
        // Kelly inputs for NewTradeModal's sizing.
        confidence:           sig?.confidence,
        riskReward:           type === 'option' ? 2 : sig?.riskReward,
      });
    } catch (err) {
      const rateLimited = err.message?.includes('Rate limited') || err.message?.includes('429');
      setTradeError(prev => ({ ...prev, [ticker]: rateLimited ? 'Rate limited — try after a minute' : 'Could not get live quote — try after a minute' }));
    }
    setTradingTicker(null);
  };

  const isFaved = (ticker) => favorites.some(f => f.ticker === ticker);

  return (
    <div className="animate-fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 'var(--r-md)',
              background: 'linear-gradient(135deg, rgba(245,158,11,0.18), rgba(245,158,11,0.06))',
              border: '1px solid rgba(245,158,11,0.25)',
            }}>
              <Star size={18} color="var(--amber)" strokeWidth={2.4} fill="var(--amber)" fillOpacity={0.4} />
            </span>
            {config.flag} Watchlist
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '6px 0 0 0' }}>
            {favorites.length} securities · Search to add
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {validateTotal > 0 && (
            <Badge tone="green" size="md" icon={Loader} outlined>
              <span style={{ marginLeft: 2 }}>Validating {validateDone}/{validateTotal}</span>
            </Badge>
          )}
          <Button
            variant="secondary" size="md" icon={Sparkles}
            loading={aiLoading} disabled={favorites.length === 0}
            onClick={handleAiAnalysis}
          >
            {aiLoading ? 'Analyzing…' : aiSummary ? 'Refresh AI' : 'Get AI Analysis'}
          </Button>
          <Button variant="ghost" size="md" icon={RefreshCw} onClick={() => loadFavorites()}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 20, maxWidth: 500 }}>
        <Search size={15} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-muted)', zIndex: 1 }} />
        <input
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search any NYSE + NASDAQ ticker or company name..."
          style={{
            width: '100%', padding: '11px 14px 11px 38px',
            borderRadius: 'var(--r-md)', border: '1px solid var(--border)',
            background: 'var(--bg-card)', color: 'var(--text-primary)',
            fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
          }}
        />
        {searchResults.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 50,
            background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)',
            border: '1px solid var(--border-strong)',
            boxShadow: 'var(--shadow-lift)', overflow: 'hidden',
            maxHeight: 320, overflowY: 'auto',
          }}>
            {searchResults.map((r) => (
              <div key={r.ticker}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)',
                  transition: 'background var(--dur-fast) var(--ease-out)',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--accent-dim)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{displayTicker(r.ticker)}</span>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', padding: '1px 5px', borderRadius: 'var(--r-xs)', background: 'var(--bg-surface)' }}>{r.type}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                </div>
                {isFaved(r.ticker) ? (
                  <span style={{ fontSize: 11, color: 'var(--amber)', flexShrink: 0, fontWeight: 600 }}>★ Added</span>
                ) : (
                  <button onClick={() => handleAdd(r.ticker, r.name)} style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '6px 12px', borderRadius: 'var(--r-sm)',
                    border: 'none', background: 'var(--grad-accent)',
                    color: '#fff', fontSize: 11, fontWeight: 700,
                    cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
                    boxShadow: '0 2px 8px var(--accent-glow)',
                  }}>
                    <Plus size={12} /> Add
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>


      {/* AI summary banner */}
      {aiError && (
        <div style={{ padding: '10px 14px', borderRadius: 'var(--r-md)', background: 'var(--red-dim)', border: '1px solid rgba(239,68,68,0.25)', color: 'var(--red-soft)', fontSize: 12, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700 }}>AI Error:</span> {aiError}
        </div>
      )}
      {aiSummary && !aiError && (
        <div style={{ padding: '10px 14px', borderRadius: 'var(--r-md)', background: 'var(--accent-dim)', border: '1px solid var(--border-strong)', color: 'var(--accent-soft)', fontSize: 12, marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <Sparkles size={14} style={{ flexShrink: 0, marginTop: 1, color: 'var(--accent-soft)' }} />
          <span>{aiSummary}</span>
        </div>
      )}

      {/* Favorites grid */}
      {loading ? (
        <div className="fav-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="card" style={{ padding: 'var(--card-pad)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div className="skeleton" style={{ height: 14, width: 14, borderRadius: '50%' }} />
                <div className="skeleton" style={{ height: 16, width: 80, borderRadius: 4 }} />
                <div className="skeleton" style={{ height: 14, width: 40, borderRadius: 3 }} />
              </div>
              <div className="skeleton skeleton-text short" style={{ marginBottom: 14 }} />
              <div className="skeleton" style={{ height: 26, width: '55%', borderRadius: 4, marginBottom: 12 }} />
              <div className="skeleton" style={{ height: 4, width: '100%', borderRadius: 2, marginBottom: 12 }} />
              <div className="skeleton" style={{ height: 50, width: '100%', borderRadius: 8, marginBottom: 8 }} />
              <div className="skeleton" style={{ height: 32, width: '100%', borderRadius: 7 }} />
            </div>
          ))}
        </div>
      ) : favorites.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
          <Star size={48} style={{ marginBottom: 12, opacity: 0.2 }} />
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>No favorites yet</div>
          <div style={{ fontSize: 13 }}>Search for any NYSE/NASDAQ ticker above to add it</div>
        </div>
      ) : (
        <div className="fav-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {favorites.map((fav) => {
            const q = quotes[fav.ticker];
            const sig = signals[fav.ticker];
            const up = (q?.changePct || 0) >= 0;
            const dirBorder = sig?.direction === 'LONG' ? 'rgba(34,197,94,0.18)'
                            : sig?.direction === 'SHORT' ? 'rgba(239,68,68,0.18)'
                            : 'var(--border-subtle)';
            return (
              <div key={fav.ticker} className="card card-hover" style={{
                padding: 'var(--card-pad)',
                border: `1px solid ${dirBorder}`,
                position: 'relative',
              }}>
                {/* Remove button */}
                <button onClick={() => handleRemove(fav.ticker)} title="Remove from watchlist" style={{
                  position: 'absolute', top: 10, right: 10, width: 24, height: 24,
                  borderRadius: 'var(--r-sm)',
                  border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                  color: 'var(--text-muted)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <X size={12} />
                </button>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, cursor: 'pointer', flexWrap: 'wrap', paddingRight: 30 }} onClick={() => setSelectedTicker(fav.ticker)}>
                  <Star size={14} color="var(--amber)" fill="var(--amber)" />
                  <span className="mono" style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>{displayTicker(fav.ticker)}</span>
                  {sig && sig.direction !== 'HOLD' && (
                    <DirBadge dir={sig.direction} size="sm" />
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fav.name}</div>

                {/* Price */}
                {q ? (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                    <span className="mono" style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{config.currencySymbol}{q.price?.toFixed(2)}</span>
                    <span className="mono" style={{ fontSize: 12, color: up ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>
                      {up ? '+' : ''}{q.changePct?.toFixed(2)}%
                    </span>
                  </div>
                ) : (
                  <div style={{ marginBottom: 12 }}>
                    <div className="skeleton" style={{ height: 24, width: '55%', borderRadius: 4, marginBottom: 4 }} />
                    <div className="skeleton" style={{ height: 11, width: '30%', borderRadius: 3 }} />
                  </div>
                )}

                {/* Confidence bar — shared ConfBar primitive, source pill */}
                {sig && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span title={TERMS.confidence} style={{ cursor: 'help' }}>Confidence</span>
                        {(() => {
                          const src = sig._confidenceSource;
                          const base = { fontSize: 8.5, padding: '2px 5px', borderRadius: 'var(--r-xs)', fontWeight: 700, letterSpacing: '0.04em' };
                          if (src === 'backtest')      return <span title={TERMS.validated}  style={{ ...base, background: 'var(--green-dim)', color: 'var(--green-soft)', cursor: 'help' }}>VALIDATED</span>;
                          if (src === 'insufficient') return <span title="Not enough price history to backtest — confidence is heuristic" style={{ ...base, background: 'var(--amber-dim)', color: 'var(--amber)', cursor: 'help' }}>LIMITED DATA</span>;
                          if (src === 'error')         return <span title="Backtest could not run — confidence is heuristic" style={{ ...base, background: 'var(--amber-dim)', color: 'var(--amber)', cursor: 'help' }}>HEURISTIC</span>;
                          if (src === 'pending')       return <span title={TERMS.validating} style={{ ...base, display: 'inline-flex', alignItems: 'center', gap: 3, background: 'var(--accent-dim)', color: 'var(--accent-soft)', cursor: 'help' }}><Loader size={8} style={{ animation: 'spin 1s linear infinite' }} />VALIDATING</span>;
                          return <span title={TERMS.confidence} style={{ ...base, background: 'var(--amber-dim)', color: 'var(--amber)', cursor: 'help' }}>HEURISTIC</span>;
                        })()}
                      </span>
                      <span className="mono" style={{
                        color: sig.confidence > 70 ? 'var(--green)' : sig.confidence > 50 ? 'var(--amber)' : 'var(--red)',
                        fontWeight: 700,
                      }}>{sig.confidence?.toFixed(0)}%</span>
                    </div>
                    <ConfBar value={sig.confidence} w="100%" />
                  </div>
                )}

                {/* Backtest results — auto-validate runs in background */}
                {sig?._confidenceSource === 'backtest' && sig._backtestResult && (
                  <div style={{
                    padding: '6px 10px', borderRadius: 'var(--r-sm)', marginBottom: 10,
                    background: 'var(--green-dim)', border: '1px solid rgba(34,197,94,0.2)',
                    fontSize: 9.5, color: 'var(--text-secondary)',
                    display: 'flex', gap: 8, flexWrap: 'wrap',
                  }}>
                    <span title={TERMS.winRate}      style={{ cursor: 'help' }}>Win <b style={{ color: 'var(--green)' }}>{sig._backtestResult.winRate}%</b></span>
                    <span title={TERMS.trials}       style={{ cursor: 'help' }}>Trials <b>{sig._backtestResult.trials}</b></span>
                    <span title={TERMS.expectancy}   style={{ cursor: 'help' }}>Avg <b style={{ color: sig._backtestResult.avgReturn >= 0 ? 'var(--green)' : 'var(--red)' }}>{sig._backtestResult.avgReturn}%</b></span>
                    <span title={TERMS.profitFactor} style={{ cursor: 'help' }}>PF <b>{sig._backtestResult.profitFactor}</b></span>
                    <span title={TERMS.sharpe}       style={{ cursor: 'help' }}>Sharpe <b>{sig._backtestResult.sharpe}</b></span>
                  </div>
                )}

                {/* Signal badge */}
                {sig && sig.direction !== 'HOLD' && (
                  <div style={{
                    padding: '8px 11px', borderRadius: 'var(--r-md)', marginBottom: 10,
                    background: sig.direction === 'LONG' ? 'var(--green-dim)' : 'var(--red-dim)',
                    border: `1px solid ${sig.direction === 'LONG' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                      {sig.direction === 'LONG' ? <TrendingUp size={13} color="var(--green)" /> : <TrendingDown size={13} color="var(--red)" />}
                      <span style={{ fontWeight: 700, color: sig.direction === 'LONG' ? 'var(--green)' : 'var(--red)' }}>
                        {sig.direction} for {sig.days} days
                      </span>
                    </div>
                    {sig.reasons?.length > 0 && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                        {sig.reasons.slice(0, 3).map((r, i) => (
                          <span key={i} style={{
                            fontSize: 9, padding: '2px 7px', borderRadius: 'var(--r-pill)',
                            background: 'var(--accent-dim)', color: 'var(--accent-soft)', fontWeight: 600,
                          }}>{r}</span>
                        ))}
                      </div>
                    )}
                    {/* Signals-page suppression notice: this directional
                        signal passed the watchlist's lenient gate but the
                        stricter Signals engine would NOT ship it. Tell the
                        user exactly why so the two pages never silently
                        contradict each other. */}
                    {sig._gateShips === false && sig._gateReason && (
                      <div
                        title={sig._gateReason}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 8,
                          padding: '6px 9px', borderRadius: 'var(--r-sm)',
                          background: 'var(--amber-dim)', border: '1px solid rgba(245,158,11,0.25)',
                          cursor: 'help',
                        }}
                      >
                        <AlertTriangle size={11} color="var(--amber)" style={{ flexShrink: 0, marginTop: 1 }} />
                        <span style={{ fontSize: 10, color: 'var(--amber)', lineHeight: 1.4 }}>
                          Not in today's signals — {sig._gateReason}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                {sig && sig.direction === 'HOLD' && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>● No strong signal — Hold</div>
                )}

                {/* AI insight strip */}
                {aiLoading && !aiInsights[fav.ticker] && sig && sig.direction !== 'HOLD' && (
                  <div className="animate-pulse-soft" style={{ height: 28, borderRadius: 'var(--r-sm)', background: 'var(--accent-dim)', border: '1px solid var(--border-strong)', marginBottom: 10 }} />
                )}
                {aiInsights[fav.ticker] && sig && sig.direction !== 'HOLD' && (
                  <div style={{ marginBottom: 10, padding: '7px 10px', borderRadius: 'var(--r-sm)', background: 'var(--accent-dim)', border: '1px solid var(--border-strong)', display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                    <span title={TERMS.aiAdjustment} style={{
                      fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap', paddingTop: 1, cursor: 'help',
                      color: aiInsights[fav.ticker].adjustment > 0 ? 'var(--green)' : aiInsights[fav.ticker].adjustment < 0 ? 'var(--red)' : 'var(--text-muted)',
                    }}>
                      AI {aiInsights[fav.ticker].adjustment > 0 ? `+${aiInsights[fav.ticker].adjustment}` : aiInsights[fav.ticker].adjustment}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.4 }}>{aiInsights[fav.ticker].reasoning}</span>
                  </div>
                )}

                {/* Options suggestion */}
                {sig?.option && (
                  <div style={{
                    padding: '8px 11px', borderRadius: 'var(--r-md)', marginBottom: 10,
                    background: 'var(--purple-dim)', border: '1px solid rgba(168,85,247,0.2)',
                  }}>
                    <div style={{ fontSize: 10, color: '#c4b5fd', fontWeight: 700, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4, letterSpacing: '0.04em' }}>
                      <Layers size={10} />OPTIONS
                    </div>
                    <div className="mono" style={{ fontSize: 11, color: '#c4b5fd' }}>
                      <span title={TERMS.optionType} style={{ cursor: 'help' }}>Buy {sig.option.type}</span> · <span title={TERMS.strike} style={{ cursor: 'help' }}>{config.currencySymbol}{sig.option.strike}</span> <span title={TERMS.expiry} style={{ cursor: 'help' }}>exp {formatDateOnly(sig.option.expiration)}</span> @ <span title={TERMS.premium} style={{ cursor: 'help' }}>{config.currencySymbol}{sig.option.premium}</span>
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>{sig.option.strategy}</div>
                    <button onClick={(e) => { e.stopPropagation(); handleTrade(fav.ticker, sig.direction, 'option', sig.option); }} disabled={tradingTicker === fav.ticker} style={{
                      width: '100%', marginTop: 6, padding: '6px 0',
                      borderRadius: 'var(--r-sm)', border: '1px solid var(--border-strong)',
                      background: tradingTicker === fav.ticker ? 'var(--bg-elevated)' : 'transparent',
                      color: tradingTicker === fav.ticker ? 'var(--text-muted)' : '#c4b5fd',
                      fontSize: 11, fontWeight: 700,
                      cursor: tradingTicker === fav.ticker ? 'wait' : 'pointer', fontFamily: 'inherit',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                    }}>
                      {tradingTicker === fav.ticker
                        ? <><Loader size={11} style={{ animation: 'spin 1s linear infinite' }} />Verifying…</>
                        : `Buy ${sig.option.type} → ${config.currencySymbol}${sig.option.premium}`}
                    </button>
                  </div>
                )}

                {/* Stock trade button */}
                {q && sig && sig.direction !== 'HOLD' && (
                  <>
                    <button onClick={(e) => { e.stopPropagation(); handleTrade(fav.ticker, sig.direction, 'stock'); }} disabled={tradingTicker === fav.ticker} style={{
                      width: '100%', padding: '10px 0',
                      borderRadius: 'var(--r-md)', border: 'none',
                      background: tradingTicker === fav.ticker ? 'var(--bg-elevated)' : (sig.direction === 'LONG' ? 'var(--grad-success)' : 'var(--grad-danger)'),
                      color: tradingTicker === fav.ticker ? 'var(--text-muted)' : '#fff',
                      fontSize: 12, fontWeight: 700,
                      cursor: tradingTicker === fav.ticker ? 'wait' : 'pointer', fontFamily: 'inherit',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      boxShadow: tradingTicker === fav.ticker ? 'none' : (sig.direction === 'LONG' ? '0 4px 14px var(--green-glow)' : '0 4px 14px var(--red-glow)'),
                    }}>
                      {tradingTicker === fav.ticker
                        ? <><Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />Verifying live price…</>
                        : <><DollarSign size={12} /> Paper Trade {sig.direction} @ {config.currencySymbol}{q.price?.toFixed(2)}</>}
                    </button>
                    {tradeError[fav.ticker] && (
                      <div style={{ fontSize: 10, color: 'var(--red-soft)', marginTop: 5, textAlign: 'center' }}>{tradeError[fav.ticker]}</div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
